import React, { Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { LoaderCircle } from 'lucide-react';
import ProtectedRoute from './ProtectedRoute';

// Lazy load components for code splitting
const SignInPage = React.lazy(() => import('../../pages/auth/SignInPage'));
const RegisterPage = React.lazy(() => import('../../pages/auth/RegisterPage'));
const ForgotPasswordPage = React.lazy(() => import('../../pages/auth/ForgotPasswordPage'));
const ResetPasswordPage = React.lazy(() => import('../../pages/auth/ResetPasswordPage'));
const VerifyEmailPage = React.lazy(() => import('../../pages/auth/VerifyEmailPage'));
const VerifyAlternateEmailPage = React.lazy(() => import('../../pages/auth/VerifyAlternateEmailPage'));
const AcceptInvitationPage = React.lazy(() => import('../../pages/auth/AcceptInvitationPage'));
const GoogleOAuthCallbackPage = React.lazy(() => import('../../pages/auth/GoogleOAuthCallbackPage'));
const MicrosoftOAuthCallbackPage = React.lazy(() => import('../../pages/auth/MicrosoftOAuthCallbackPage'));

// Dashboard pages
const AdminDashboard = React.lazy(() => import('../../pages/dashboard/AdminDashboard'));

// staff pages
const RolesPermissionsPage = React.lazy(() => import('../../pages/staff/staff-management/RolesPermissionsPage'));
const AllCoursesPage = React.lazy(() => import('../../pages/staff/courses/AllCoursesPage'));
const PendingApprovalPage = React.lazy(() => import('../../pages/staff/courses/PendingApprovalPage'));
const ArchivedCoursesPage = React.lazy(() => import('../../pages/staff/courses/ArchivedCoursesPage'));
const CategoriesPage = React.lazy(() => import('../../pages/staff/courses/CategoriesPage'));
const CreateCoursePage = React.lazy(() => import('../../pages/staff/courses/CreateCoursePage'));
const ViewCoursePage = React.lazy(() => import('../../pages/staff/courses/ViewCoursePage'));
const EditCoursePage = React.lazy(() => import('../../pages/staff/courses/EditCoursePage'));
const CourseFeedbackQuestionsPage = React.lazy(
  () => import('../../pages/staff/courses/CourseFeedbackQuestionsPage')
);
const CourseCurriculumSectionsPage = React.lazy(
  () => import('../../pages/staff/courses/CourseCurriculumSectionsPage')
);
const CourseSectionsPage = React.lazy(
  () => import('../../pages/staff/course-sections/CourseSectionsPage')
);
const CreateCourseSectionPage = React.lazy(
  () => import('../../pages/staff/course-sections/CreateCourseSectionPage')
);
const ViewCourseSectionPage = React.lazy(
  () => import('../../pages/staff/course-sections/ViewCourseSectionPage')
);
const EditCourseSectionPage = React.lazy(
  () => import('../../pages/staff/course-sections/EditCourseSectionPage')
);
const ViewEnrollmentPage = React.lazy(() => import('../../pages/staff/enrollments/ViewEnrollmentPage'));
const EnrollmentsPage = React.lazy(() => import('../../pages/staff/enrollments/EnrollmentsPage'));
const CreateEnrollmentPage = React.lazy(() => import('../../pages/staff/enrollments/CreateEnrollmentPage'));
const EditEnrollmentPage = React.lazy(() => import('../../pages/staff/enrollments/EditEnrollmentPage'));
const EnrollmentRequestsPage = React.lazy(() => import('../../pages/staff/enrollments/EnrollmentRequestsPage'));
const StaffManagementPage = React.lazy(() => import('../../pages/staff/staff-management/StaffManagementPage'));
const ViewStaffPage = React.lazy(() => import('../../pages/staff/staff-management/ViewStaffPage'));
const StaffMemberAuditLogsPage = React.lazy(() => import('../../pages/staff/staff-management/StaffMemberAuditLogsPage'));
const CreateStaffPage = React.lazy(() => import('../../pages/staff/staff-management/CreateStaffPage'));
const EditStaffPage = React.lazy(() => import('../../pages/staff/staff-management/EditStaffPage'));
const StudentsPage = React.lazy(() => import('../../pages/staff/students/StudentsPage'));
const CreateStudentPage = React.lazy(() => import('../../pages/staff/students/CreateStudentPage'));
const BulkUploadStudentsPage = React.lazy(() => import('../../pages/staff/students/BulkUploadStudentsPage'));
const ViewStudentPage = React.lazy(() => import('../../pages/staff/students/ViewStudentPage'));
const StudentAuditLogsPage = React.lazy(() => import('../../pages/staff/students/StudentAuditLogsPage'));
const EditStudentPage = React.lazy(() => import('../../pages/staff/students/EditStudentPage'));
const ResultsPage = React.lazy(() => import('../../pages/staff/students/results/ResultsPage'));
const ViewResultPage = React.lazy(() => import('../../pages/staff/students/results/ViewResultPage'));
const EditResultPage = React.lazy(() => import('../../pages/staff/students/results/EditResultPage'));
const GradesPage = React.lazy(() => import('../../pages/staff/students/grades/GradesPage'));
const ViewGradePage = React.lazy(() => import('../../pages/staff/students/grades/ViewGradePage'));
const CreateGradePage = React.lazy(() => import('../../pages/staff/students/grades/CreateGradePage'));
const EditGradePage = React.lazy(() => import('../../pages/staff/students/grades/EditGradePage'));
const CertificatesPage = React.lazy(() => import('../../pages/staff/students/certificates/CertificatesPage'));
const ViewCertificatePage = React.lazy(() => import('../../pages/staff/students/certificates/ViewCertificatePage'));
const StudentApplicationsPage = React.lazy(() => import('../../pages/staff/students/applications/StudentApplicationsPage'));
const ViewStudentApplicationPage = React.lazy(() => import('../../pages/staff/students/applications/ViewStudentApplicationPage'));
const ApplicationWaitlistPage = React.lazy(() => import('../../pages/staff/students/applications/waitlist/ApplicationWaitlistPage'));
const ViewWaitlistEntryPage = React.lazy(() => import('../../pages/staff/students/applications/waitlist/ViewWaitlistEntryPage'));
const FinancialAidApplicationsPage = React.lazy(() => import('../../pages/staff/students/financial-aid/FinancialAidApplicationsPage'));
const ViewFinancialAidApplicationPage = React.lazy(() => import('../../pages/staff/students/financial-aid/ViewFinancialAidApplicationPage'));
const InstructorsPage = React.lazy(() => import('../../pages/staff/instructors/InstructorsPage'));
const ViewInstructorPage = React.lazy(() => import('../../pages/staff/instructors/ViewInstructorPage'));
const InstructorAuditLogsPage = React.lazy(() => import('../../pages/staff/instructors/InstructorAuditLogsPage'));
const CreateInstructorPage = React.lazy(() => import('../../pages/staff/instructors/CreateInstructorPage'));
const EditInstructorPage = React.lazy(() => import('../../pages/staff/instructors/EditInstructorPage'));
const BulkUploadInstructorsPage = React.lazy(() => import('../../pages/staff/instructors/BulkUploadInstructorsPage'));
const TasksPage = React.lazy(() => import('../../pages/staff/tasks/TasksPage'));
const ViewTaskPage = React.lazy(() => import('../../pages/staff/tasks/ViewTaskPage'));
const CreateTaskPage = React.lazy(() => import('../../pages/staff/tasks/CreateTaskPage'));
const EditTaskPage = React.lazy(() => import('../../pages/staff/tasks/EditTaskPage'));
const SubmissionsPage = React.lazy(() => import('../../pages/staff/tasks/submissions/SubmissionsPage'));
const ViewSubmissionPage = React.lazy(() => import('../../pages/staff/tasks/submissions/ViewSubmissionPage'));
const CreateSubmissionPage = React.lazy(() => import('../../pages/staff/tasks/submissions/CreateSubmissionPage'));
const GradeSubmissionPage = React.lazy(() => import('../../pages/staff/tasks/submissions/GradeSubmissionPage'));
const TicketsPage = React.lazy(() => import('../../pages/staff/tickets/TicketsPage'));
const ViewTicketPage = React.lazy(() => import('../../pages/staff/tickets/ViewTicketPage'));
const CreateTicketPage = React.lazy(() => import('../../pages/staff/tickets/CreateTicketPage'));
const BadgesPage = React.lazy(() => import('../../pages/staff/badges/BadgesPage'));
const ViewBadgePage = React.lazy(() => import('../../pages/staff/badges/ViewBadgePage'));
const CreateBadgePage = React.lazy(() => import('../../pages/staff/badges/CreateBadgePage'));
const EditBadgePage = React.lazy(() => import('../../pages/staff/badges/EditBadgePage'));
const PaymentsPage = React.lazy(() => import('../../pages/staff/financial/PaymentsPage'));
const ViewPaymentPage = React.lazy(() => import('../../pages/staff/financial/ViewPaymentPage'));
const EditPaymentPage = React.lazy(() => import('../../pages/staff/financial/EditPaymentPage'));
const PaymentGatewaysPage = React.lazy(() => import('../../pages/staff/financial/PaymentGatewaysPage'));
const CreatePaymentGatewayPage = React.lazy(() => import('../../pages/staff/financial/CreatePaymentGatewayPage'));
const ViewPaymentGatewayPage = React.lazy(() => import('../../pages/staff/financial/ViewPaymentGatewayPage'));
const EditPaymentGatewayPage = React.lazy(() => import('../../pages/staff/financial/EditPaymentGatewayPage'));
const CouponsDiscountsPage = React.lazy(() => import('../../pages/staff/financial/CouponsDiscountsPage'));
const CreateDiscountPage = React.lazy(() => import('../../pages/staff/financial/CreateDiscountPage'));
const ViewDiscountPage = React.lazy(() => import('../../pages/staff/financial/ViewDiscountPage'));
const EditDiscountPage = React.lazy(() => import('../../pages/staff/financial/EditDiscountPage'));
const CourseQuotesPage = React.lazy(() => import('../../pages/staff/financial/CourseQuotesPage'));
const CreateCourseQuotePage = React.lazy(() => import('../../pages/staff/financial/CreateCourseQuotePage'));
const ViewCourseQuotePage = React.lazy(() => import('../../pages/staff/financial/ViewCourseQuotePage'));
const PaymentPlansPage = React.lazy(() => import('../../pages/staff/financial/PaymentPlansPage'));
const ViewPaymentPlanPage = React.lazy(() => import('../../pages/staff/financial/ViewPaymentPlanPage'));
const CreatePaymentPlanPage = React.lazy(() => import('../../pages/staff/financial/CreatePaymentPlanPage'));
const ProductsPage = React.lazy(() => import('../../pages/staff/financial/ProductsPage'));
const CreateProductPage = React.lazy(() => import('../../pages/staff/financial/CreateProductPage'));
const EditProductPage = React.lazy(() => import('../../pages/staff/financial/EditProductPage'));
const ViewProductPage = React.lazy(() => import('../../pages/staff/financial/ViewProductPage'));
const ProductPurchasesPage = React.lazy(() => import('../../pages/staff/financial/ProductPurchasesPage'));
const FinancialReportsPage = React.lazy(() => import('../../pages/staff/financial/FinancialReportsPage'));
const DepartmentsPage = React.lazy(() => import('../../pages/staff/departments/DepartmentsPage'));
const AnnouncementsPage = React.lazy(() => import('../../pages/staff/announcements/AnnouncementsPage'));
const ViewAnnouncementPage = React.lazy(() => import('../../pages/staff/announcements/ViewAnnouncementPage'));
const AnnouncementCategoriesPage = React.lazy(
  () => import('../../pages/staff/announcements/AnnouncementCategoriesPage')
);
const EventsPage = React.lazy(() => import('../../pages/staff/events/EventsPage'));
const CreateEventPage = React.lazy(() => import('../../pages/staff/events/CreateEventPage'));
const ViewEventPage = React.lazy(() => import('../../pages/staff/events/ViewEventPage'));
const EditEventPage = React.lazy(() => import('../../pages/staff/events/EditEventPage'));
const ChatRoomsPage = React.lazy(() => import('../../pages/staff/chat/rooms/ChatRoomsPage'));
const CreateChatRoomPage = React.lazy(() => import('../../pages/staff/chat/rooms/CreateChatRoomPage'));
const ViewChatRoomPage = React.lazy(() => import('../../pages/staff/chat/rooms/ViewChatRoomPage'));
const EditChatRoomPage = React.lazy(() => import('../../pages/staff/chat/rooms/EditChatRoomPage'));
const RoomMessagesPage = React.lazy(() => import('../../pages/staff/chat/rooms/RoomMessagesPage'));
const ChatManagementPage = React.lazy(() => import('../../pages/staff/chat/management/ChatManagementPage'));
const BlockedUsersPage = React.lazy(() => import('../../pages/staff/chat/management/BlockedUsersPage'));
const FlaggedMessagesPage = React.lazy(() => import('../../pages/staff/chat/management/FlaggedMessagesPage'));
const ArchivedChatsPage = React.lazy(() => import('../../pages/staff/chat/management/ArchivedChatsPage'));
const KnowledgeBasePage = React.lazy(() => import('../../pages/staff/knowledge-base/KnowledgeBasePage'));

const EnrollmentReportsPage = React.lazy(() => import('../../pages/staff/reports/EnrollmentReportsPage'));
const FinancialReportsPageReports = React.lazy(() => import('../../pages/staff/reports/FinancialReportsPage'));
const AuditLogsPage = React.lazy(() => import('../../pages/staff/reports/AuditLogsPage'));
const ViewAuditLogPage = React.lazy(() => import('../../pages/staff/reports/ViewAuditLogPage'));
const SettingsPage = React.lazy(() => import('../../pages/staff/settings/SettingsPage'));
const InstitutionSettingsAuditLogsPage = React.lazy(
  () => import('../../pages/staff/settings/InstitutionSettingsAuditLogsPage')
);
const BrandingPage = React.lazy(() => import('../../pages/staff/settings/BrandingPage'));
const ProfilePage = React.lazy(() => import('../../pages/staff/settings/ProfilePage'));
const SecuritySettingsPage = React.lazy(() => import('../../pages/staff/settings/SecuritySettingsPage'));
const NotificationSettingsPage = React.lazy(() => import('../../pages/staff/settings/NotificationSettingsPage'));
const UserSessionsPage = React.lazy(() => import('../../pages/staff/security/UserSessionsPage'));
const ViewSessionPage = React.lazy(() => import('../../pages/staff/security/ViewSessionPage'));
const ViewUserSessionsPage = React.lazy(() => import('../../pages/staff/security/ViewUserSessionsPage'));
const EmailTemplatesPage = React.lazy(() => import('../../pages/staff/settings/EmailTemplatesPage'));
const CreateEmailTemplatePage = React.lazy(() => import('../../pages/staff/settings/CreateEmailTemplatePage'));
const EditEmailTemplatePage = React.lazy(() => import('../../pages/staff/settings/EditEmailTemplatePage'));
const ViewEmailTemplatePage = React.lazy(() => import('../../pages/staff/settings/ViewEmailTemplatePage'));
const OAuthIntegrationsPage = React.lazy(() => import('../../pages/staff/settings/OAuthIntegrationsPage'));
const CreateOAuthIntegrationPage = React.lazy(() => import('../../pages/staff/settings/CreateOAuthIntegrationPage'));
const ViewOAuthIntegrationPage = React.lazy(() => import('../../pages/staff/settings/ViewOAuthIntegrationPage'));
const AnalyticsPage = React.lazy(() => import('../../pages/staff/AnalyticsPage'));
const NotificationsPage = React.lazy(() => import('../../pages/staff/notifications/NotificationsPage'));
const ProgrammesPage = React.lazy(() => import('../../pages/staff/programmes/ProgrammesPage'));
const CreateProgrammePage = React.lazy(() => import('../../pages/staff/programmes/CreateProgrammePage'));
const ViewProgrammePage = React.lazy(() => import('../../pages/staff/programmes/ViewProgrammePage'));
const EditProgrammePage = React.lazy(() => import('../../pages/staff/programmes/EditProgrammePage'));
const LessonsPage = React.lazy(() => import('../../pages/staff/lessons/LessonsPage'));
const CreateLessonPage = React.lazy(() => import('../../pages/staff/lessons/CreateLessonPage'));
const ViewLessonPage = React.lazy(() => import('../../pages/staff/lessons/ViewLessonPage'));
const EditLessonPage = React.lazy(() => import('../../pages/staff/lessons/EditLessonPage'));
const LessonMaterialsPage = React.lazy(() => import('../../pages/staff/lessons/materials/LessonMaterialsPage'));
const CreateLessonMaterialPage = React.lazy(() => import('../../pages/staff/lessons/materials/CreateLessonMaterialPage'));
const ViewLessonMaterialPage = React.lazy(() => import('../../pages/staff/lessons/materials/ViewLessonMaterialPage'));
const EditLessonMaterialPage = React.lazy(() => import('../../pages/staff/lessons/materials/EditLessonMaterialPage'));
const AcademicSessionsPage = React.lazy(() => import('../../pages/staff/academic-sessions/AcademicSessionsPage'));
const CreateAcademicSessionPage = React.lazy(() => import('../../pages/staff/academic-sessions/CreateAcademicSessionPage'));
const ViewAcademicSessionPage = React.lazy(() => import('../../pages/staff/academic-sessions/ViewAcademicSessionPage'));
const EditAcademicSessionPage = React.lazy(() => import('../../pages/staff/academic-sessions/EditAcademicSessionPage'));
const CourseRevisionsPage = React.lazy(() => import('../../pages/staff/course-revisions/CourseRevisionsPage'));
const CreateCourseRevisionPage = React.lazy(() => import('../../pages/staff/course-revisions/CreateCourseRevisionPage'));
const ViewCourseRevisionPage = React.lazy(() => import('../../pages/staff/course-revisions/ViewCourseRevisionPage'));
const EditCourseRevisionPage = React.lazy(() => import('../../pages/staff/course-revisions/EditCourseRevisionPage'));
const AttendanceSessionsPage = React.lazy(() => import('../../pages/staff/attendance/sessions/AttendanceSessionsPage'));
const CreateAttendanceSessionPage = React.lazy(() => import('../../pages/staff/attendance/sessions/CreateAttendanceSessionPage'));
const ViewAttendanceSessionPage = React.lazy(() => import('../../pages/staff/attendance/sessions/ViewAttendanceSessionPage'));
const EditAttendanceSessionPage = React.lazy(() => import('../../pages/staff/attendance/sessions/EditAttendanceSessionPage'));
const AttendanceRecordsPage = React.lazy(() => import('../../pages/staff/attendance/records/AttendanceRecordsPage'));
const CreateAttendanceRecordPage = React.lazy(() => import('../../pages/staff/attendance/records/CreateAttendanceRecordPage'));
const BulkCreateAttendanceRecordsPage = React.lazy(() => import('../../pages/staff/attendance/records/BulkCreateAttendanceRecordsPage'));
const ViewAttendanceRecordPage = React.lazy(() => import('../../pages/staff/attendance/records/ViewAttendanceRecordPage'));
const AttendancePoliciesPage = React.lazy(() => import('../../pages/staff/attendance/policies/AttendancePoliciesPage'));
const CreateAttendancePolicyPage = React.lazy(() => import('../../pages/staff/attendance/policies/CreateAttendancePolicyPage'));
const ViewAttendancePolicyPage = React.lazy(() => import('../../pages/staff/attendance/policies/ViewAttendancePolicyPage'));
const EditAttendancePolicyPage = React.lazy(() => import('../../pages/staff/attendance/policies/EditAttendancePolicyPage'));
const CreatePolicyOverridePage = React.lazy(() => import('../../pages/staff/attendance/policies/CreatePolicyOverridePage'));

// Layout components
const AuthLayout = React.lazy(() => import('../../layouts/AuthLayout'));
const DashboardLayout = React.lazy(() => import('../../layouts/DashboardLayout'));

// Loading component
const LoadingFallback = () => (
  <div className="flex items-center justify-center min-h-screen">
    <LoaderCircle className="h-8 w-8 animate-spin text-primary" />
  </div>
);

const AppRouter: React.FC = () => {
  return (
    <BrowserRouter>
      <Suspense fallback={<LoadingFallback />}>
        <Routes>
          {/* Root route - SignInPage as index */}
          <Route path="/" element={<AuthLayout />}>
            <Route index element={<SignInPage />} />
          </Route>
          
          {/* Public routes */}
          <Route path="/auth/*" element={<AuthLayout />}>
            <Route path="login" element={<SignInPage />} />
            <Route path="register" element={<RegisterPage />} />
            <Route path="enrollments" element={<EnrollmentsPage />} />
            <Route path="enrollments/:id" element={<ViewEnrollmentPage />} />
            <Route path="forgot-password" element={<ForgotPasswordPage />} />
            <Route path="reset-password" element={<ResetPasswordPage />} />
            <Route path="verify-email" element={<VerifyEmailPage />} />
            <Route path="verify-alternate-email" element={<VerifyAlternateEmailPage />} />
            <Route path="accept-invitation" element={<AcceptInvitationPage />} />
            <Route path="google-oauth" element={<GoogleOAuthCallbackPage />} />
            <Route path="microsoft-oauth" element={<MicrosoftOAuthCallbackPage />} />
          </Route>

          {/* Direct link supports - Preserve search params */}
          <Route 
            path="/verify-email" 
            element={<Navigate to={`/auth/verify-email${window.location.search}`} replace />} 
          />
          <Route 
            path="/verify-alternate-email" 
            element={<Navigate to={`/auth/verify-alternate-email${window.location.search}`} replace />} 
          />
          <Route 
            path="/accept-invitation" 
            element={<Navigate to={`/auth/accept-invitation${window.location.search}`} replace />} 
          />

            {/* Protected routes with dashboard layout */}
            <Route
              element={
                <ProtectedRoute>
                  <DashboardLayout />
                </ProtectedRoute>
              }
            >
              {/* Default dashboard routes - redirect to staff */}
              <Route path="/dashboard" element={<Navigate to="/staff" replace />} />
              <Route path="/instructor" element={<Navigate to="/staff" replace />} />
              
              {/* staff Dashboard */}
              <Route path="/staff" element={<AdminDashboard />} />
              <Route path="/staff/notifications" element={<NotificationsPage />} />

              {/* Analytics */}
              <Route element={<ProtectedRoute requiredPermission="analytics:view" />}>
                <Route path="/staff/analytics" element={<AnalyticsPage />} />
              </Route>

              {/* Staff Management */}
              <Route element={<ProtectedRoute requiredPermission="user:list" />}>
                <Route path="/staff/staff-management" element={<StaffManagementPage />} />
                <Route path="/staff/staff-management/create" element={<CreateStaffPage />} />
                <Route path="/staff/staff-management/:id/audit-logs" element={<StaffMemberAuditLogsPage />} />
                <Route path="/staff/staff-management/:id" element={<ViewStaffPage />} />
                <Route path="/staff/staff-management/:id/edit" element={<EditStaffPage />} />
                <Route path="/staff/staff-management/roles" element={<ProtectedRoute requiredPermission="role:read"><RolesPermissionsPage /></ProtectedRoute>} />
              </Route>

              {/* Students Management */}
              <Route element={<ProtectedRoute requiredPermission="user:list" />}>
                <Route path="/staff/students" element={<StudentsPage />} />
                <Route path="/staff/students/applications" element={<StudentApplicationsPage />} />
                <Route path="/staff/students/applications/waitlist" element={<ApplicationWaitlistPage />} />
                <Route path="/staff/students/applications/waitlist/:id" element={<ViewWaitlistEntryPage />} />
                <Route path="/staff/students/applications/:id" element={<ViewStudentApplicationPage />} />
                <Route
                  path="/staff/students/financial-aid"
                  element={
                    <ProtectedRoute requiredPermission="application:list">
                      <FinancialAidApplicationsPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/staff/students/financial-aid/:id"
                  element={
                    <ProtectedRoute requiredPermission={['application:read', 'application:manage']}>
                      <ViewFinancialAidApplicationPage />
                    </ProtectedRoute>
                  }
                />
                <Route path="/staff/students/create" element={<CreateStudentPage />} />
                <Route path="/staff/students/bulk-upload" element={<BulkUploadStudentsPage />} />
                <Route path="/staff/students/:id/audit-logs" element={<StudentAuditLogsPage />} />
                <Route path="/staff/students/:id" element={<ViewStudentPage />} />
                <Route path="/staff/students/:id/edit" element={<EditStudentPage />} />
                <Route path="/staff/students/results" element={<ResultsPage />} />
                <Route path="/staff/students/results/enrollment/:enrollmentId" element={<ViewResultPage />} />
                <Route path="/staff/students/results/:id" element={<ViewResultPage />} />
                <Route path="/staff/students/results/:id/edit" element={<EditResultPage />} />
                <Route path="/staff/students/grades" element={<GradesPage />} />
                <Route path="/staff/students/grades/create" element={<CreateGradePage />} />
                <Route path="/staff/students/grades/:id" element={<ViewGradePage />} />
                <Route path="/staff/students/grades/:id/edit" element={<EditGradePage />} />
                <Route path="/staff/students/certificates" element={<CertificatesPage />} />
                <Route path="/staff/students/certificates/:id" element={<ViewCertificatePage />} />
              </Route>

              {/* Instructors Management */}
              <Route element={<ProtectedRoute requiredPermission="user:list" />}>
                <Route path="/staff/instructors" element={<InstructorsPage />} />
                <Route path="/staff/instructors/create" element={<CreateInstructorPage />} />
                <Route path="/staff/instructors/bulk-upload" element={<BulkUploadInstructorsPage />} />
                <Route path="/staff/instructors/:id/audit-logs" element={<InstructorAuditLogsPage />} />
                <Route path="/staff/instructors/:id" element={<ViewInstructorPage />} />
                <Route path="/staff/instructors/:id/edit" element={<EditInstructorPage />} />
              </Route>

              {/* Course Management */}
              <Route element={<ProtectedRoute requiredPermission="course:list" />}>
                <Route path="/staff/courses" element={<AllCoursesPage />} />
                <Route path="/staff/courses/create" element={<CreateCoursePage />} />
                <Route path="/staff/courses/pending" element={<PendingApprovalPage />} />
                <Route path="/staff/courses/archived" element={<ArchivedCoursesPage />} />
                <Route path="/staff/courses/categories" element={<CategoriesPage />} />
                <Route path="/staff/courses/:id/feedback-questions" element={<CourseFeedbackQuestionsPage />} />
                <Route path="/staff/courses/:id/sections" element={<CourseCurriculumSectionsPage />} />
                <Route path="/staff/courses/:id" element={<ViewCoursePage />} />

                {/* Course Curriculum */}
                <Route path="/staff/course-sections" element={<CourseSectionsPage />} />
                <Route path="/staff/course-sections/create" element={<CreateCourseSectionPage />} />
                <Route path="/staff/course-sections/:id/edit" element={<EditCourseSectionPage />} />
                <Route path="/staff/course-sections/:id" element={<ViewCourseSectionPage />} />
                <Route path="/staff/courses/:id/edit" element={<EditCoursePage />} />
                
                {/* Course Revisions */}
                <Route path="/staff/course-revisions" element={<CourseRevisionsPage />} />
                <Route path="/staff/course-revisions/create" element={<CreateCourseRevisionPage />} />
                <Route path="/staff/course-revisions/:id" element={<ViewCourseRevisionPage />} />
                <Route path="/staff/course-revisions/:id/edit" element={<EditCourseRevisionPage />} />

                {/* Programmes */}
                <Route path="/staff/programmes" element={<ProgrammesPage />} />
                <Route path="/staff/programmes/create" element={<CreateProgrammePage />} />
                <Route path="/staff/programmes/:id" element={<ViewProgrammePage />} />
                <Route path="/staff/programmes/:id/edit" element={<EditProgrammePage />} />

                {/* Lessons */}
                <Route path="/staff/lessons" element={<LessonsPage />} />
                <Route path="/staff/lessons/create" element={<CreateLessonPage />} />
                <Route path="/staff/lessons/:id" element={<ViewLessonPage />} />
                <Route path="/staff/lessons/:id/edit" element={<EditLessonPage />} />
                <Route path="/staff/lessons/:lessonId/materials" element={<LessonMaterialsPage />} />
                <Route path="/staff/lessons/:lessonId/materials/create" element={<CreateLessonMaterialPage />} />
                <Route path="/staff/lessons/:lessonId/materials/:materialId" element={<ViewLessonMaterialPage />} />
                <Route path="/staff/lessons/:lessonId/materials/:materialId/edit" element={<EditLessonMaterialPage />} />
                
                {/* Tasks */}
                <Route path="/staff/tasks" element={<TasksPage />} />
                <Route path="/staff/tasks/create" element={<CreateTaskPage />} />
                <Route path="/staff/tasks/:id" element={<ViewTaskPage />} />
                <Route path="/staff/tasks/:id/edit" element={<EditTaskPage />} />
                <Route path="/staff/tasks/submissions" element={<SubmissionsPage />} />
                <Route path="/staff/tasks/submissions/create" element={<CreateSubmissionPage />} />
                <Route path="/staff/tasks/submissions/:id" element={<ViewSubmissionPage />} />
                <Route path="/staff/tasks/submissions/:id/grade" element={<GradeSubmissionPage />} />
              </Route>

              {/* Enrollment & Academic Sessions */}
              <Route element={<ProtectedRoute requiredPermission="enrollment:list" />}>
                <Route path="/staff/enrollments" element={<EnrollmentsPage />} />
                <Route path="/staff/enrollments/create" element={<CreateEnrollmentPage />} />
                <Route path="/staff/enrollments/requests" element={<EnrollmentRequestsPage />} />
                <Route path="/staff/enrollments/requests/:id" element={<ViewEnrollmentPage />} />
                <Route path="/staff/enrollments/:id" element={<ViewEnrollmentPage />} />
                <Route path="/staff/enrollments/:id/edit" element={<EditEnrollmentPage />} />
              </Route>

              <Route element={<ProtectedRoute requiredPermission="academic_session:read" />}>
                <Route path="/staff/academic-sessions" element={<AcademicSessionsPage />} />
                <Route path="/staff/academic-sessions/create" element={<CreateAcademicSessionPage />} />
                <Route path="/staff/academic-sessions/:id" element={<ViewAcademicSessionPage />} />
                <Route path="/staff/academic-sessions/:id/edit" element={<EditAcademicSessionPage />} />
              </Route>

              {/* Attendance Management */}
              <Route element={<ProtectedRoute requiredPermission="attendance_record:read" />}>
                <Route path="/staff/attendance/sessions" element={<AttendanceSessionsPage />} />
                <Route path="/staff/attendance/sessions/create" element={<CreateAttendanceSessionPage />} />
                <Route path="/staff/attendance/sessions/:id" element={<ViewAttendanceSessionPage />} />
                <Route path="/staff/attendance/sessions/:id/edit" element={<EditAttendanceSessionPage />} />
                <Route path="/staff/attendance/records" element={<AttendanceRecordsPage />} />
                <Route path="/staff/attendance/records/create" element={<CreateAttendanceRecordPage />} />
                <Route path="/staff/attendance/records/bulk-create" element={<BulkCreateAttendanceRecordsPage />} />
                <Route path="/staff/attendance/records/:id" element={<ViewAttendanceRecordPage />} />
              </Route>

              <Route element={<ProtectedRoute requiredPermission="attendance_policy:read" />}>
                <Route path="/staff/attendance/policies" element={<AttendancePoliciesPage />} />
                <Route path="/staff/attendance/policies/create" element={<CreateAttendancePolicyPage />} />
                <Route path="/staff/attendance/policies/override" element={<CreatePolicyOverridePage />} />
                <Route path="/staff/attendance/policies/:id" element={<ViewAttendancePolicyPage />} />
                <Route path="/staff/attendance/policies/:id/edit" element={<EditAttendancePolicyPage />} />
              </Route>

              {/* Financial Management */}
              <Route element={<ProtectedRoute requiredPermission="payment:list" />}>
                <Route path="/staff/financial/payments" element={<PaymentsPage />} />
                <Route path="/staff/payments/:id" element={<ViewPaymentPage />} />
                <Route path="/staff/payments/:id/edit" element={<EditPaymentPage />} />
                <Route path="/staff/payment-gateways" element={<PaymentGatewaysPage />} />
                <Route path="/staff/payment-gateways/create" element={<CreatePaymentGatewayPage />} />
                <Route path="/staff/payment-gateways/:id" element={<ViewPaymentGatewayPage />} />
                <Route path="/staff/payment-gateways/:id/edit" element={<EditPaymentGatewayPage />} />
                <Route path="/staff/financial/reports" element={<FinancialReportsPage />} />
              </Route>

              {/* Coupons & Discounts */}
              <Route element={<ProtectedRoute requiredPermission="discount:list" />}>
                <Route path="/staff/financial/coupons" element={<CouponsDiscountsPage />} />
              </Route>
              <Route
                path="/staff/financial/coupons/create"
                element={
                  <ProtectedRoute requiredPermission="discount:create">
                    <CreateDiscountPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/staff/financial/coupons/:id"
                element={
                  <ProtectedRoute requiredPermission={['discount:read', 'discount:list']}>
                    <ViewDiscountPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/staff/financial/coupons/:id/edit"
                element={
                  <ProtectedRoute requiredPermission="discount:update">
                    <EditDiscountPage />
                  </ProtectedRoute>
                }
              />

              {/* Quotes */}
              <Route element={<ProtectedRoute requiredPermission="course_quote:list" />}>
                <Route path="/staff/financial/course-quotes" element={<CourseQuotesPage />} />
              </Route>
              <Route
                path="/staff/financial/course-quotes/create"
                element={
                  <ProtectedRoute requiredPermission="course_quote:create">
                    <CreateCourseQuotePage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/staff/financial/course-quotes/:id"
                element={
                  <ProtectedRoute requiredPermission={['course_quote:read', 'course_quote:list']}>
                    <ViewCourseQuotePage />
                  </ProtectedRoute>
                }
              />

              {/* Payment Plans */}
              <Route element={<ProtectedRoute requiredPermission="payment_plan:list" />}>
                <Route path="/staff/financial/payment-plans" element={<PaymentPlansPage />} />
              </Route>
              <Route
                path="/staff/financial/payment-plans/:id"
                element={
                  <ProtectedRoute requiredPermission={['payment_plan:read', 'payment_plan:list']}>
                    <ViewPaymentPlanPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/staff/financial/payment-plans/create"
                element={
                  <ProtectedRoute requiredPermission="payment_plan:create">
                    <CreatePaymentPlanPage />
                  </ProtectedRoute>
                }
              />

              {/* Products */}
              <Route element={<ProtectedRoute requiredPermission="product:list" />}>
                <Route path="/staff/financial/products" element={<ProductsPage />} />
                <Route path="/staff/financial/products/purchases" element={<ProductPurchasesPage />} />
              </Route>
              <Route
                path="/staff/financial/products/create"
                element={
                  <ProtectedRoute requiredPermission="product:create">
                    <CreateProductPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/staff/financial/products/:id"
                element={
                  <ProtectedRoute requiredPermission={['product:read', 'product:list']}>
                    <ViewProductPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/staff/financial/products/:id/edit"
                element={
                  <ProtectedRoute requiredPermission="product:update">
                    <EditProductPage />
                  </ProtectedRoute>
                }
              />

              {/* Communication */}
              <Route element={<ProtectedRoute requiredPermission="announcement:read" />}>
                <Route path="/staff/announcements" element={<AnnouncementsPage />} />
                <Route path="/staff/announcements/categories" element={<ProtectedRoute requiredPermission="announcement:manage"><AnnouncementCategoriesPage /></ProtectedRoute>} />
                <Route path="/staff/announcements/:id" element={<ViewAnnouncementPage />} />
                <Route path="/staff/events" element={<EventsPage />} />
                <Route path="/staff/events/create" element={<CreateEventPage />} />
                <Route path="/staff/events/:id/edit" element={<EditEventPage />} />
                <Route path="/staff/events/:id" element={<ViewEventPage />} />
                <Route path="/staff/knowledge-base" element={<KnowledgeBasePage />} />
              </Route>

              {/* Support Tickets */}
              <Route element={<ProtectedRoute requiredPermission="ticket:read" />}>
                <Route path="/staff/tickets" element={<TicketsPage />} />
                <Route path="/staff/tickets/create" element={<CreateTicketPage />} />
                <Route path="/staff/tickets/:id" element={<ViewTicketPage />} />
              </Route>

              {/* Chat */}
              <Route element={<ProtectedRoute requiredPermission="chat:read" />}>
                <Route path="/staff/chat/rooms" element={<ChatRoomsPage />} />
                <Route path="/staff/chat/rooms/create" element={<CreateChatRoomPage />} />
                <Route path="/staff/chat/rooms/:id" element={<ViewChatRoomPage />} />
                <Route path="/staff/chat/rooms/:id/edit" element={<EditChatRoomPage />} />
                <Route path="/staff/chat/rooms/:id/messages" element={<RoomMessagesPage />} />
                
                <Route element={<ProtectedRoute requiredPermission="chat:manage_settings" />}>
                  <Route path="/staff/chat/management" element={<ChatManagementPage />} />
                  <Route path="/staff/chat/management/blocked-users" element={<BlockedUsersPage />} />
                  <Route path="/staff/chat/management/flagged-messages" element={<FlaggedMessagesPage />} />
                  <Route path="/staff/chat/management/archived-chats" element={<ArchivedChatsPage />} />
                </Route>
              </Route>

              {/* Reports & Audit */}
              <Route path="/staff/reports" element={<Navigate to="/staff/reports/audit" replace />} />
              <Route path="/staff/reports/financial" element={<FinancialReportsPageReports />} />
              <Route path="/staff/reports/enrollments" element={<EnrollmentReportsPage />} />
              <Route element={<ProtectedRoute requiredPermission="system:logs" />}>
                <Route path="/staff/reports/audit" element={<AuditLogsPage />} />
                <Route path="/staff/reports/audit/:id" element={<ViewAuditLogPage />} />
              </Route>

              {/* Settings */}
              <Route element={<ProtectedRoute requiredPermission="system:config" />}>
                <Route path="/staff/settings" element={<SettingsPage />} />
                <Route path="/staff/settings/institution/audit-logs" element={<InstitutionSettingsAuditLogsPage />} />
                <Route path="/staff/settings/branding" element={<BrandingPage />} />
                <Route path="/staff/settings/email-templates" element={<EmailTemplatesPage />} />
                <Route path="/staff/settings/email-templates/create" element={<CreateEmailTemplatePage />} />
                <Route path="/staff/settings/email-templates/:id" element={<ViewEmailTemplatePage />} />
                <Route path="/staff/settings/email-templates/:id/edit" element={<EditEmailTemplatePage />} />
                <Route path="/staff/oauth/integrations" element={<OAuthIntegrationsPage />} />
                <Route path="/staff/oauth/integrations/create" element={<CreateOAuthIntegrationPage />} />
                <Route path="/staff/oauth/integrations/:id" element={<ViewOAuthIntegrationPage />} />
                <Route path="/staff/oauth/integrations/:id/edit" element={<CreateOAuthIntegrationPage />} />
              </Route>

              {/* Shared/Common Settings */}
              <Route path="/staff/settings/profile" element={<ProfilePage />} />
              <Route path="/staff/settings/notifications" element={<NotificationSettingsPage />} />
              <Route path="/staff/settings/security" element={<SecuritySettingsPage />} />
              <Route path="/staff/security/sessions" element={<UserSessionsPage />} />
              <Route path="/staff/security/sessions/:sessionId" element={<ViewSessionPage />} />
              <Route path="/staff/security/sessions/user/:userId" element={<ViewUserSessionsPage />} />
              
              <Route path="/staff/badges" element={<ProtectedRoute requiredPermission="badge:read"><BadgesPage /></ProtectedRoute>} />
              <Route path="/staff/badges/create" element={<ProtectedRoute requiredPermission="badge:read"><CreateBadgePage /></ProtectedRoute>} />
              <Route path="/staff/badges/:id" element={<ProtectedRoute requiredPermission="badge:read"><ViewBadgePage /></ProtectedRoute>} />
              <Route path="/staff/badges/:id/edit" element={<ProtectedRoute requiredPermission="badge:read"><EditBadgePage /></ProtectedRoute>} />
              
              <Route path="/staff/departments" element={<ProtectedRoute requiredPermission="department:read"><DepartmentsPage /></ProtectedRoute>} />
            </Route>

          {/* Catch all route - redirect to home (sign in) */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
    </BrowserRouter>
  );
};

export default AppRouter;